/**
 * Home Feature - Traditional Chinese (zh-TW)
 */

import type { HomeLocale } from './types';

const zhTW: HomeLocale = {
    home: {
        title: '用 AI 加速您的專利撰寫',
        subtitle: '從交底書到審查意見答覆，智能完成專利全流程文書',

        tabs: {
            patent: '專利申請',
            disclosure: '交底書',
            oar: '審查意見答覆',
        },

        cardTitles: {
            patent: '專利申請',
            disclosure: '交底書',
            oar: '審查意見答覆',
        },

        placeholders: {
            patent: '請描述您的技術方案、解決的問題以及創新之處。\n\n您也可以輸入 {\'@\'} 使用模板或上傳檔案。',
            disclosure: '請描述您的發明構思、解決的技術問題以及關鍵創新點。\n\n您也可以輸入 {\'@\'} 使用模板或上傳檔案。',
            oar: '請在此貼上審查意見通知書內容，或描述您需要答覆的駁回理由。',
        },

        placeholder: {
            default: '描述您的發明或輸入 {\'@\'} 使用模板',
            template: '輸入 {\'@\'} 瀏覽專利模板',
            example1: '例如：一種用於圖像識別的機器學習系統...',
            example2: '例如：一種高容量改進型電池設計...',
            example3: '例如：{\'@\'} USPTO 軟體專利模板',
        },

        workMode: {
            agent: '智能體',
            copilot: '助手',
        },

        actions: {
            addFiles: '添加檔案',
            addFilesTooltip: '上傳檔案（PDF、Word、TXT）',
            readOnlyTip: '您只有唯讀權限，無法上傳檔案',
            dropToAdd: '放開以添加檔案',
            style: '風格',
            styleTooltip: '選擇撰寫風格',
            template: '模板',
            templateTooltip: '選擇專利模板',
            voiceInput: '語音輸入',
            voiceInputTooltip: '語音輸入',
            voiceInputSoon: '語音輸入功能即將推出...',
            generate: '開始',
            loggingIn: '登入中...',
            startDrafting: '開始撰寫',
            draftingStyle: '撰寫風格',
            selectTemplate: '選擇模板',
            removeFile: '移除',
            loginNow: '立即登入',
            changeTemplate: '點擊更改模板',
            featureDisabledTip: '您沒有該功能的使用權限，請聯絡管理員',
            personalQuotaExhaustedTip: '您的個人額度已用完，請聯絡管理員分配額度',
        },

        upload: {
            dragOrClick: '拖曳檔案到此處，或點擊上傳',
            supportedFormats: '支援 PDF、Word、TXT 及圖片檔案',
        },

        quickStart: {
            title: '快速開始常用撰寫任務：',
            disclosure: '撰寫交底書',
            specification: '撰寫說明書',
            claims: '撰寫權利要求',
            oar: '撰寫答覆',
            figures: '設計附圖',
        },

        messages: {
            filesAdded: '已添加 {count} 個檔案',
            pleaseEnterContent: '請輸入內容或上傳檔案',
            pleaseUploadFile: '請上傳審查意見檔案',
            generating: '正在生成專利文檔...',
            navigationFailed: '導航失敗',
            styleApplied: '已應用風格：{name}',
            templateApplied: '已選擇模板：{name}',
            mentionApplied: '已選擇{type}：{name}',
            templateLoading: '正在載入模板：{name}',
            submitFailed: '提交失敗，請重試',
            invalidFile: '無效的檔案',
            uploadFailed: '檔案上傳失敗，請重試',
            filesUploading: '檔案正在上傳中，請稍候',
            fileStatusUploading: '上傳中...',
            fileStatusFailed: '上傳失敗',
            someFilesNotUploaded: '部分檔案上傳失敗，請重新上傳',
            emptyInput: '請輸入內容或上傳檔案',
            emptyOarInput: '請輸入專利申請號或上傳檔案',
            fileProcessed: '檔案 {name} 解析完成',
            fileProcessFailed: '檔案 {name} 解析失敗',
            fileProcessingTimeout: '檔案解析逾時，請重新上傳',
            filesProcessing: '檔案正在解析中，請稍候...',
            filesNotReady: '部分檔案尚未解析完成，請等待後再建立任務',
            fileAlreadyExists: '檔案 {name} 已存在，直接使用',
            defaultPromptForPatent: '請解析上傳的文檔，生成技術交底書分析報告和專利申請文檔。',
            defaultPromptForDisclosure: '按照技術交底書分析的標準流程處理檔案，生成技術交底書',
            loginRequired: '請先登入後再開始撰寫，您的工作將儲存到帳戶中。',
        },

        // 統一的第一句話配置（根據任務類型和場景自動選擇）
        initialPrompts: {
            patent_draft: {
                withFiles: '請解析上傳的文檔，生成技術交底書分析報告和專利申請文檔。',
                empty: '請開始撰寫專利申請',
            },
            disclosure: {
                withFiles: '按照技術交底書分析的標準流程處理檔案，生成技術交底書',
                empty: '請開始撰寫技術交底書',
            },
            prosecution: {
                withPatentNumber: '開始分析審查意見（申請號：{patentNumber}）',
                withFiles: '開始分析審查意見',
                empty: '請分析審查意見並生成答覆檔案',
            },
        },

        mention: {
            title: '模板',
            selectType: '選擇類型',
            styleTitle: '風格',
            priorArt: '現有技術',
            file: '檔案',
            document: '文檔',
            loading: '載入中...',
            navigate: '導航',
            select: '選擇',
            close: '關閉',
            noResults: '未找到 "{query}" 相關結果',
            tryDifferent: '嘗試其他搜尋詞',
            applicationDate: '申請:',
            publicationDate: '公開:',
            docTypes: {
                patent_draft: '專利撰寫',
                disclosure: '技術交底書',
                prosecution: '答覆審查意見',
            },
            docStatus: {
                draft: '草稿',
                in_review: '審核中',
                approved: '已批准',
                filed: '已提交',
            },
            types: {
                template: {
                    label: '模板',
                    desc: '專利申請模板',
                },
                style: {
                    label: '撰寫風格',
                    desc: '撰寫風格設定',
                },
                priorArt: {
                    label: '現有技術',
                    desc: '現有技術專利',
                },
                file: {
                    label: '檔案',
                    desc: '附件檔案',
                },
                document: {
                    label: '文檔',
                    desc: '專案文檔',
                },
            },
            templates: {
                usptoUtilityPatent: 'USPTO 實用專利',
                usptoSoftwarePatent: 'USPTO 軟體專利',
                epoPatentApplication: 'EPO 專利申請',
                pctInternationalApplication: 'PCT 國際申請',
                cnipaPatentApplication: 'CNIPA 專利申請',
            },
        },

        tooltips: {
            uploadFile: '上傳檔案',
            voiceInput: '語音輸入',
        },

        patent: {
            jurisdiction: '請選擇申請地區',
        },

        oar: {
            title: '審查意見答覆',
            projectTitle: '審查意見答覆 - {patentNumber}',
            projectTitleWithoutNumber: '審查意見答覆',
            initialPrompt: '開始分析審查意見',
            initialPromptWithNumber: '開始分析審查意見（申請號：{patentNumber}）',
            confirmMessage: '已確認OAR分析結果，請繼續處理',
            oarExtractList: 'OAR 提取清單',
            rejectionTableTitle: '駁回分析表',
            rejectionAnalysis: {
                title: '駁回分析',
                status: {
                    extracting: '提取資料中',
                    processing: '處理中',
                },
                button: {
                    confirm: '確認並繼續',
                    saving: '儲存中',
                    addClause: '新增條款',
                    showMore: '展開全部',
                    showLess: '收起',
                    export: '匯出 Excel',
                    exportSheetName: '駁回分析',
                    viewDetail: '查看邏輯鏈',
                    delete: '刪除',
                },
                detail: {
                    drawerTitle: '分析詳情',
                    examinerOpinion: '審查員意見',
                    featureComparison: '特徵對比',
                    claimFeature: '本申請特徵',
                    priorArtFeature: '對比文件特徵',
                    priorArtLabel: '對比文件{no} · {name}',
                    noPriorArt: '無對比文件',
                    strategy: '答覆策略',
                    strategyReason: '策略依據',
                    prevItem: '上一條',
                    nextItem: '下一條',
                },
                basicInfo: {
                    title: '基礎資訊',
                    application: '申請號',
                    oaNo: '審查意見編號',
                    type: '類型',
                    examiner: '審查員',
                    issueDate: '發文日',
                    priorArt: '對比文件',
                    applicationFiles: '申請文件',
                },
                tabs: {
                    all: '全部',
                },
                tableHeaders: {
                    claim: '權利要求',
                    statute: '法條',
                    claimAndStatute: '權利要求/法條',
                    claimElement: '權利要求要素',
                    examinerReasoning: '審查員理由',
                    aiAnalysis: 'AI分析',
                    strategy: '答覆策略',
                },
                statusPills: {
                    agree: '同意',
                    unsure: '不確定',
                    disagree: '不同意',
                },
                direction: {
                    agree: '同意',
                    unsure: '不確定',
                    disagree: '不同意',
                },
                labels: {
                    direction: '答覆方向',
                    strategy: '答覆策略',
                    claimAmendment: '權利要求修改',
                    amendmentMethod: '修改方式',
                    amendmentBasis: '修改依據',
                },
                needAmendment: {
                    yes: '修改',
                    no: '不修改',
                    optional: 'AI判斷',
                },
                tooltips: {
                    direction: '是否認同審查員觀點',
                    strategy: '針對該駁回理由的答覆策略',
                    claimAmendment: '是否需要修改權利要求',
                },
                placeholder: {
                    edit: '點擊編輯...',
                    claimElement: '輸入權利要求要素...',
                    examinerReasoning: '輸入審查員意見...',
                    strategy: '輸入答覆策略...',
                    amendmentMethod: '輸入修改方式...',
                    amendmentBasis: '輸入修改依據...',
                },
                emptyState: {
                    noData: '暫無駁回資料',
                    hint: '提取完成後將在此顯示駁回分析',
                },
                exportHeaders: {
                    claim: '權利要求',
                    statute: '法律條款',
                    claimElement: '權利要求要素',
                    examinerReasoning: '審查員意見',
                    direction: '答覆方向',
                    strategy: '答覆策略',
                    distinguishingFeatures: '區別技術特徵',
                    priorArt: '對比文件',
                    needAmendment: '是否需要修改',
                    amendmentMethod: '修改方式',
                    amendmentBasis: '修改依據',
                },
                message: {
                    noDataId: '無法取得資料ID',
                    noData: '暫無資料',
                    saveSuccess: '儲存成功',
                    saveFailed: '儲存失敗',
                    deleteSuccess: '刪除成功',
                },
            },
            region: {
                uspto: 'USPTO',
                epo: 'EPO',
                cnipa: 'CNIPA',
            },
            patentNumber: {
                label: '專利申請號',
                placeholder: '輸入被審查專利的申請號，以獲取最新審查意見',
            },
            uploadAction: {
                title: '上傳審查意見',
                choose: '選擇檔案',
                description: '上傳需要答覆的審查意見PDF/Word文件',
                uploading: '正在上傳',
                addMore: '繼續上傳更多檔案',
            },
            uploadedFiles: {
                title: '已上傳檔案 ({count})',
            },
            fileStatus: {
                preparing: '準備檔案...',
                analyzing: '分析內容...',
                finalizing: '即將完成...',
                processing: '處理中...',
                estimatedTime: '預計需要 1-2 分鐘',
                completed: '已完成',
                failed: '解析失敗',
            },
            actions: {
                agent: 'Agent',
                copilot: 'Copilot',
                start: '開始',
            },
            search: {
                failed: '專利搜尋失敗，請重試',
                notFound: '未找到專利',
            },
            patent: {
                publicationNumber: '公開(公告)號：',
                publicationDate: '公開公告日：',
                currentApplicant: '[標]當前申請(專利權)人：',
            },
            download: {
                searching: '正在搜尋審查意見PDF...',
                downloading: 'PDF下載已開始，請檢查瀏覽器下載',
                success: 'PDF下載成功',
                failed: 'PDF下載失敗，請重試',
                notFound: '未找到審查意見PDF，您可以手動上傳',
                visitUsptoWebsite: '訪問 USPTO 網站下載',
            },
            unsupportedRegion: '暫不支援該地區',
            prosecutionTip: '輸入專利申請號/上傳審查意見PDF，即可開始',
            rejectionDetail: {
                drawerTitle: '駁回詳情',
                examinerOpinion: '審查員意見',
                featureComparison: '特徵對比',
                claimFeature: '權利要求特徵',
                specificationSource: '說明書原文',
                priorArtFeature: '對比文件特徵',
                priorArtLabel: '對比文件{no}（{name}）',
                noPriorArt: '暫無對比文件資訊',
                priorArtSource: '{name} 原文',
                distinguishingFeatures: '區別技術特徵',
                aiStrategy: 'AI 答覆策略',
                strategyReason: '策略依據',
                crossValidation: '交叉驗證',
                prevItem: '上一條',
                nextItem: '下一條',
                direction: {
                    agree: '同意',
                    unsure: '待定',
                    disagree: '不同意',
                },
                claimAmendment: {
                    title: '權利要求修改建議',
                    needAmendment: '是否需要修改',
                    yes: '修改',
                    no: '不修改',
                    optional: 'AI判斷',
                    method: '修改方式',
                    basis: '修改依據',
                },
            },
        },

        draftingStyle: {
            title: '選擇撰寫風格',
            stylesHeader: '風格列表',
            previewHeader: '預覽',
            cancel: '取消',
            apply: '套用風格',
            loading: '載入中...',
            loadingPreview: '載入預覽中...',
            loadError: '載入風格列表失敗',
            noStyles: '暫無可用風格',
            noPreview: '暫無預覽內容',
            pleaseSelect: '請先選擇風格',
            used: '已使用 {count} 次',
            styles: {
                standard: {
                    name: '標準格式',
                    description: '傳統專利格式，帶編號段落和正式語言',
                },
                detailed: {
                    name: '詳細技術',
                    description: '全面的技術描述，包含豐富的實施例細節',
                },
                concise: {
                    name: '簡潔權利要求導向',
                    description: '精簡格式，強調清晰有力的權利要求語言',
                },
                international: {
                    name: 'PCT 國際申請',
                    description: '針對 PCT 國際專利申請優化的格式',
                },
                continuation: {
                    name: '延續申請',
                    description: '引用母案申請的延續或分案申請格式',
                },
            },
        },

        templateSelection: {
            title: '選擇模板',
            templatesHeader: '模板列表',
            previewHeader: '預覽',
            cancel: '取消',
            apply: '應用模板',
            loading: '載入中...',
            loadingPreview: '載入預覽中...',
            loadError: '載入模板列表失敗',
            loadDetailError: '載入模板詳情失敗',
            noTemplates: '暫無可用模板',
            noPreview: '暫無預覽內容',
            pleaseSelect: '請先選擇模板',
        },

        documentModifications: {
            title: '文件修改已套用',
            summary: '共 {count} 條修改',
            streamingSummary: '正在生成修改，已識別 {count} 處',
            streamingTitle: 'AI 正在編輯文件',
            streamingDesc: '正在接收修改內容，後端完成後會自動合併並刷新最新版本。',
            streamPreview: '即時修改流',
            appliedSummary: '已套用 {count} 處修改',
            appliedTitle: '修改已自動套用',
            appliedDesc: '後端已完成文件合併並建立新版本，編輯器會刷新為最新內容。',
            emptyState: '暫無修改明細',
            noContent: '無新增內容',
            oldContent: '舊文字：',
            actions: {
                insert: '插入',
                replace: '替換',
                delete: '刪除',
            },
            tableHeaders: {
                index: '#',
                documentId: '文件 ID',
                action: '操作',
                dataId: 'data-id',
                content: '修改內容',
                oldContent: '舊文字',
            },
            exportExcel: '匯出 Excel',
        },
    },
};

export default zhTW;
